(() => {
  const ROOT_SELECTOR = '[data-st-root]';

  function publish(name, data) {
    try {
      window.Shopify?.analytics?.publish?.(name, data);
    } catch (error) {
      console.debug('[JBS Steamer] Analytics event skipped', error);
    }
  }

  function showToast(root, message, error = false) {
    const toast = root.querySelector('[data-st-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.style.borderColor = error ? '#ef4444' : '#496dff';
    toast.hidden = false;
    window.clearTimeout(toast._stTimer);
    toast._stTimer = window.setTimeout(() => { toast.hidden = true; }, 3600);
  }

  async function addVariant(root, variantId) {
    const rootUrl = window.Shopify?.routes?.root || '/';
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections },
    }));

    const formData = new FormData();
    formData.set('id', String(variantId));
    formData.set('quantity', '1');
    formData.set('sections', sections.join(','));
    formData.set('sections_url', `${rootUrl}products/${root.dataset.productHandle}`);
    if (root.dataset.afterAdd === 'drawer') formData.set('on_success', 'force_open_drawer');

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    const response = await fetch(`${rootUrl}cart/add.js`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const responseJson = await response.json().catch(() => ({}));
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

    if (!response.ok) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      throw new Error(responseJson.description || responseJson.message || 'This variant could not be added.');
    }

    if (root.dataset.afterAdd === 'cart' || window.themeVariables?.settings?.cartType === 'page') {
      window.location.assign(`${rootUrl}cart`);
      return responseJson;
    }

    const cart = await fetch(`${rootUrl}cart.js`).then((result) => result.json());
    cart.sections = responseJson.sections || {};
    const successMode = root.dataset.afterAdd === 'drawer' ? 'force_open_drawer' : null;

    root.dispatchEvent(new CustomEvent('variant:add', {
      bubbles: true,
      detail: {
        items: Object.prototype.hasOwnProperty.call(responseJson, 'items') ? responseJson.items : [responseJson],
        cart,
        onSuccessDo: successMode,
      },
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'variant:add', onSuccessDo: successMode, cart },
    }));
    return responseJson;
  }

  function initialize(root) {
    if (!root || root.dataset.stInitialized === 'true') return;
    root.dataset.stInitialized = 'true';

    const variantButtons = Array.from(root.querySelectorAll('[data-st-variant]'));
    const mainImage = root.querySelector('.st-main-image');
    const purchaseCard = root.querySelector('[data-st-purchase-card]');
    const sticky = root.querySelector('[data-st-sticky]');
    let selectedVariant = variantButtons.find((button) => button.classList.contains('is-selected')) || variantButtons[0];
    let busy = false;

    function updateVariant(button, track = true) {
      if (!button) return;
      selectedVariant = button;
      const available = button.dataset.available === 'true';
      const price = button.dataset.variantPrice || '';

      variantButtons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-selected', active);
        item.setAttribute('aria-pressed', String(active));
      });
      root.querySelectorAll('[data-st-price], [data-st-sticky-price]').forEach((node) => { const temp = document.createElement('div'); temp.innerHTML = price; node.textContent = temp.textContent; });

      root.querySelectorAll('[data-st-add], [data-st-sticky-add]').forEach((node) => {
        node.disabled = !available;
      });
      root.querySelectorAll('[data-st-add-label]').forEach((node) => {
        node.textContent = available ? 'Add to cart' : 'Available at launch';
      });

      const url = new URL(window.location.href);
      url.searchParams.set('variant', button.dataset.variantId);
      window.history.replaceState({}, '', url);
      if (track) publish('jbs_steamer_variant_select', {
        product_id: root.dataset.productId,
        variant_id: button.dataset.variantId,
        plug: button.dataset.variantTitle,
        price,
      });
    }

    variantButtons.forEach((button) => button.addEventListener('click', () => updateVariant(button)));
    if (selectedVariant) updateVariant(selectedVariant, false);

    root.querySelectorAll('[data-st-thumb]').forEach((button, index) => {
      button.addEventListener('click', () => {
        if (!mainImage || button.classList.contains('is-active')) return;
        root.querySelectorAll('[data-st-thumb]').forEach((item) => item.classList.toggle('is-active', item === button));
        mainImage.classList.add('is-changing');
        window.setTimeout(() => {
          mainImage.src = button.dataset.imageSrc;
          mainImage.srcset = button.dataset.imageSrcset || '';
          mainImage.alt = button.dataset.imageAlt || '';
          mainImage.classList.remove('is-changing');
        }, 120);
        root.querySelector('.st-media-frame')?.classList.toggle('is-treatment', index > 0);
        publish('jbs_steamer_gallery_select', { index: index + 1 });
      });
    });

    async function handleAdd(button) {
      const variantId = Number(selectedVariant?.dataset.variantId || 0);
      if (!variantId || busy || button.disabled) {
        if (!variantId) showToast(root, 'Choose a valid plug variant first.', true);
        return;
      }

      busy = true;
      root.querySelectorAll('[data-st-add], [data-st-sticky-add]').forEach((node) => { node.disabled = true; });
      try {
        await addVariant(root, variantId);
        publish('jbs_steamer_add_to_cart', {
          product_id: root.dataset.productId,
          variant_id: variantId,
          plug: selectedVariant.dataset.variantTitle,
        });
        if (root.dataset.afterAdd === 'stay') showToast(root, `${selectedVariant.dataset.variantTitle} plug added to cart.`);
      } catch (error) {
        showToast(root, error.message || 'Unable to add this item.', true);
      } finally {
        busy = false;
        const available = selectedVariant?.dataset.available === 'true';
        root.querySelectorAll('[data-st-add], [data-st-sticky-add]').forEach((node) => { node.disabled = !available; });
      }
    }

    root.querySelectorAll('[data-st-add], [data-st-sticky-add]').forEach((button) => {
      button.addEventListener('click', () => handleAdd(button));
    });

    root.querySelectorAll('[data-st-scroll-buy]').forEach((button) => {
      button.addEventListener('click', () => purchaseCard?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    });

    const serviceInput = root.querySelector('[data-st-service-input]');
    const dailyInput = root.querySelector('[data-st-daily-input]');
    const daysInput = root.querySelector('[data-st-days-input]');
    function updateCalculator() {
      if (!serviceInput || !dailyInput || !daysInput) return;
      const service = Number(serviceInput.value);
      const daily = Number(dailyInput.value);
      const days = Number(daysInput.value);
      root.querySelector('[data-st-service-price]').textContent = String(service);
      root.querySelector('[data-st-daily-count]').textContent = String(daily);
      root.querySelector('[data-st-days-count]').textContent = String(days);
      root.querySelector('[data-st-monthly-revenue]').textContent = new Intl.NumberFormat(document.documentElement.lang || 'de-DE').format(service * daily * days);
    }
    [serviceInput, dailyInput, daysInput].forEach((input) => input?.addEventListener('input', updateCalculator));
    updateCalculator();

    if (sticky && purchaseCard && 'IntersectionObserver' in window) {
      sticky.hidden = false;
      const observer = new IntersectionObserver(([entry]) => {
        sticky.classList.toggle('is-visible', !entry.isIntersecting && window.scrollY > purchaseCard.offsetTop);
      }, { threshold: 0.08 });
      observer.observe(purchaseCard);
    }

    publish('jbs_steamer_landing_view', {
      product_id: root.dataset.productId,
      section_id: root.dataset.sectionId,
      language: document.documentElement.lang,
    });
  }

  function initializeAll(container = document) {
    container.querySelectorAll(ROOT_SELECTOR).forEach(initialize);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll());
  else initializeAll();

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
